/**
 * Time + layout helpers for the calendar grid.
 *
 * All time math goes through here so DST / time-zone bugs only have one
 * place to live. New helpers added by later phases (snapTo15, locale-aware
 * formatting) land here too.
 */

interface IntervalLike {
  id: string;
  start_at: string;
  end_at: string;
}

/**
 * Group overlapping intervals into "lanes" so they can be rendered
 * side-by-side instead of stacked on top of each other.
 *
 * Returns a Map keyed by interval id → { columnIndex, columnCount }.
 *
 * Algorithm:
 *  1. Sort by start time.
 *  2. Walk through; for each event, find the lowest-index lane whose last
 *     event has already ended; place it there. If none free, allocate a new
 *     lane.
 *  3. After placement, every event in an "overlap cluster" gets the same
 *     columnCount (= max lanes used in that cluster), and its columnIndex
 *     stays as its assigned lane.
 *
 * Handles partial overlaps: A[2:00–3:00], B[2:30–3:30], C[2:45–4:00] all
 * end up in 3 lanes with columnCount=3 even though A and C don't overlap.
 */
export function groupOverlaps<T extends IntervalLike>(
  events: T[],
): Map<string, { columnIndex: number; columnCount: number }> {
  const result = new Map<string, { columnIndex: number; columnCount: number }>();
  if (events.length === 0) return result;

  const sorted = [...events].sort(
    (a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime(),
  );

  // Build clusters: contiguous groups where each event overlaps with at
  // least one other in the cluster (transitively).
  const clusters: T[][] = [];
  let current: T[] = [];
  let currentMaxEnd = 0;

  for (const ev of sorted) {
    const start = new Date(ev.start_at).getTime();
    const end = new Date(ev.end_at).getTime();

    if (current.length === 0 || start < currentMaxEnd) {
      current.push(ev);
      currentMaxEnd = Math.max(currentMaxEnd, end);
    } else {
      clusters.push(current);
      current = [ev];
      currentMaxEnd = end;
    }
  }
  if (current.length > 0) clusters.push(current);

  // Lane-pack each cluster.
  for (const cluster of clusters) {
    const laneEnds: number[] = []; // laneEnds[i] = end-time of last event in lane i
    const assignments = new Map<string, number>();

    for (const ev of cluster) {
      const start = new Date(ev.start_at).getTime();
      const end = new Date(ev.end_at).getTime();

      let lane = laneEnds.findIndex((e) => e <= start);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(end);
      } else {
        laneEnds[lane] = end;
      }
      assignments.set(ev.id, lane);
    }

    const columnCount = laneEnds.length;
    for (const [id, columnIndex] of assignments) {
      result.set(id, { columnIndex, columnCount });
    }
  }

  return result;
}
